export type MediaCachePolicy = "immutableArtworkMedia" | "mutableManagedAsset" | "unknownExternal";

const MUTABLE_MANAGED_PATHS = [
  "/3d-files",
  "/artwork-model/",
  "/gallery-hdri/",
  "/space-model/",
  "/mood/sky/",
  "/mood/light-map/",
  "/mood/material-textures/",
  "/mood/lut-cube/",
];

const IMMUTABLE_ARTWORK_PATHS = [
  "/artworks/images/",
  "/artworks/original-images/",
  "/artworks/3dmodels/",
  "/artworks/videos/",
  "/artworks/sounds/",
];

function normalizedPath(url: string) {
  try {
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    return new URL(url, baseUrl).pathname;
  } catch {
    return url;
  }
}

export function classifyMediaCachePolicy(url: string): MediaCachePolicy {
  const path = normalizedPath(url);
  if (MUTABLE_MANAGED_PATHS.some((prefix) => path.includes(prefix))) {
    return "mutableManagedAsset";
  }
  if (IMMUTABLE_ARTWORK_PATHS.some((prefix) => path.includes(prefix))) {
    return "immutableArtworkMedia";
  }
  return "unknownExternal";
}

export function shouldCacheTextureUrl(url: string) {
  return classifyMediaCachePolicy(url) === "immutableArtworkMedia";
}

export function getMediaFetchOptions(url: string): RequestInit {
  return classifyMediaCachePolicy(url) === "mutableManagedAsset"
    ? { cache: "no-store" }
    : {};
}

export function createMutableAssetCacheBypassKey() {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function getMutableManagedAssetUrl(url: string, cacheBypassKey: string) {
  if (classifyMediaCachePolicy(url) !== "mutableManagedAsset" || !cacheBypassKey) {
    return url;
  }

  try {
    const isAbsolute = /^(?:[a-z]+:)?\/\//i.test(url);
    const isRootRelative = url.startsWith("/");
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const parsedUrl = new URL(url, baseUrl);
    parsedUrl.searchParams.set("artetraCacheBust", cacheBypassKey);

    if (isAbsolute) {
      return parsedUrl.toString();
    }

    const relativeUrl = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    return isRootRelative ? relativeUrl : relativeUrl.replace(/^\//, "");
  } catch {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}artetraCacheBust=${encodeURIComponent(cacheBypassKey)}`;
  }
}

export function getArtworkMediaUrl(url: string) {
  return url;
}
